/**
 * Der Vertrag zwischen Frontend, Backend und Kartenarchiv an einer Stelle.
 *
 * Diese Adressen und Namen tauchen an mehreren Stellen auf - im Stil, im
 * Offline-Speicher, im Service Worker und in den Tests. Wenn sie
 * auseinanderlaufen, lädt die Karte nichts und niemand sieht warum. Deshalb
 * stehen sie hier und nur hier.
 */

import type { Bounds } from './viewport'

/** Adresse des Kachelarchivs. Muss zu basemapRoute im Backend passen. */
export const TILES_URL = '/tiles/basemap.pmtiles'

/**
 * Adresse für den Komplettdownload.
 *
 * Bewusst dieselbe Datei mit einem Anhängsel: der Service Worker fängt
 * /tiles/basemap.pmtiles ab, um Bereichsanfragen aus dem Cache zu beantworten,
 * und würde sonst auch diesen Download abfangen und ein zweites Mal ablegen -
 * 371 MB doppelt. Die Regel im Service Worker endet auf dem Dateinamen und
 * greift deshalb bei dieser Adresse nicht.
 */
export const TILES_DOWNLOAD_URL = `${TILES_URL}?vollstaendig=1`

/**
 * Name des Cache-Speichers für das Archiv.
 *
 * Muss mit cacheName im Workbox-Abschnitt von vite.config.ts übereinstimmen:
 * die App legt das Archiv hinein, der Service Worker liest daraus.
 */
export const TILES_CACHE = 'roadbook-karte'

/**
 * Adresse der mitgelieferten Glyphen. Siehe public/glyphs/HERKUNFT.txt.
 *
 * MapLibre setzt Beschriftungen aus vorberechneten Abstandsfeldern, die je
 * Zeichenbereich als Protobuf geholt werden. Eine fremde Glyphenadresse wäre
 * zweimal falsch - die CSP erlaubt nur die eigene Herkunft, und im Funkloch
 * gäbe es keine Ortsnamen. Deshalb liegen die Bereiche der Route im eigenen
 * Auslieferungsverzeichnis.
 *
 * Fehlt ein Bereich, fällt MapLibre je Zeichen auf TinySDF zurück und
 * zeichnet es aus der Systemschrift. Das ist der Grund, warum hier nur ein
 * gutes halbes Megabyte liegt statt der vollen sechs: was wir nicht
 * mitliefern, fehlt nicht, es sieht nur etwas anders aus - und kostet eine
 * vergebliche Anfrage. Deshalb liegen die Bereiche der Route vollstaendig da,
 * rumänisch samt 512-767.
 */
export const GLYPHS_URL = '/glyphs/{fontstack}/{range}.pbf'

/**
 * Der einzige Schriftsatz, für den Glyphen mitliefern.
 *
 * Der Name ist gleichzeitig der Verzeichnisname unter public/glyphs und der
 * Wert von text-font im Stil - MapLibre setzt ihn wörtlich in GLYPHS_URL ein.
 * Deshalb kleingeschrieben und ohne Leerzeichen, obwohl die Schrift
 * "Noto Sans Regular" heisst.
 */
export const FONT = 'noto-sans-regular'

/**
 * Namensnennung. Nicht abschaltbar und nicht verhandelbar: die Daten stehen
 * unter der ODbL, die die Nennung der Quelle verlangt.
 */
export const ATTRIBUTION = '© OpenStreetMap contributors'

/** Welche der beiden Karten gezeigt wird. */
export type MapMode = 'live' | 'schematic'

/**
 * Der Bereich des Kachelarchivs, aus dessen Kopf gelesen.
 *
 * Der PMTiles-Leser wird dafür nachgeladen und nicht fest eingebunden: er
 * hängt am Kartenbündel, und der erste Start der App soll ihn nicht bezahlen.
 * Die Anfrage selbst kostet ein paar Kilobyte am Anfang der Datei - dieselben,
 * die MapLibre gleich darauf ohnehin holt, und aus demselben Cache.
 *
 * @returns null, wenn der Kopf nicht zu lesen war. Kein Grund zur Aufregung:
 *   dann bleibt es bei der echten Karte, wie bisher.
 */
export async function basemapBereich(): Promise<Bounds | null> {
  try {
    const { PMTiles } = await import('pmtiles')
    const kopf = await new PMTiles(TILES_URL).getHeader()
    const werte = [kopf.minLon, kopf.minLat, kopf.maxLon, kopf.maxLat]
    if (!werte.every((wert) => Number.isFinite(wert))) return null
    return [
      [kopf.minLon, kopf.minLat],
      [kopf.maxLon, kopf.maxLat],
    ]
  } catch (err) {
    console.warn('Bereich des Kartenarchivs liess sich nicht lesen', err)
    return null
  }
}

export interface MapModeInput {
  /** Liegt auf dem Server überhaupt ein Kachelarchiv? Aus /api/v1/config. */
  hasBasemap: boolean
  /** Kann dieser Browser WebGL? Ohne WebGL gibt es keine MapLibre-Karte. */
  webgl: boolean
  /** Hat der Nutzer die schematische Karte ausdrücklich gewählt? */
  prefersSchematic: boolean
  /**
   * Deckt das Archiv die Gegend dieser Reise ab?
   *
   * Wahr, solange es niemand besser weiss: der Bereich des Archivs kommt aus
   * dessen Kopf und damit über das Netz. Bis er da ist, wird die echte Karte
   * nicht zurückgehalten.
   */
  basemapPasst?: boolean
}

/**
 * Entscheidet zwischen echter und schematischer Karte.
 *
 * Die schematische Karte ist keine Notlösung, sondern die Rückfallebene, die
 * immer funktioniert: ohne Archiv, ohne WebGL, im Druck. Deshalb entscheidet
 * diese Funktion im Zweifel für sie. Sie ist absichtlich rein - die vier
 * Gründe, aus denen die echte Karte ausfällt, sind der einzige Teil dieser
 * Kartenlogik, der wirklich Verhalten enthält, und der soll prüfbar sein.
 */
export function decideMapMode(input: MapModeInput): MapMode {
  if (input.prefersSchematic) return 'schematic'
  if (!input.webgl) return 'schematic'
  if (!input.hasBasemap) return 'schematic'
  if (input.basemapPasst === false) return 'schematic'
  return 'live'
}

/**
 * Liegt die Route wenigstens teilweise im Bereich des Kachelarchivs?
 *
 * Das Archiv wird ausdrücklich nicht je Reise gehalten - es ist einige hundert
 * Megabyte gross, und der Betreiber hat den Platz nicht. Es deckt also die
 * Gegend ab, für die es geschnitten wurde, und für eine zweite Reise
 * anderswohin kann das die falsche sein. Sichtbar wäre das sonst nur als graue
 * Fläche mit einer Route darauf: MapLibre meldet keinen Fehler, wenn es alle
 * angefragten Kacheln bekommt und darin schlicht nichts steht.
 *
 * Es genügt eine Überschneidung, keine vollständige Abdeckung. Eine Reise, die
 * am Rand des Archivs beginnt und darüber hinausfährt, ist auf der echten
 * Karte über die erste Hälfte hinweg besser aufgehoben als auf der
 * schematischen - und dass es dann irgendwann grau wird, sieht man selbst.
 *
 * @param archiv der Bereich aus dem Kopf des Archivs, oder null wenn er nicht
 *   zu ermitteln war. Dann fällt die Antwort zugunsten der echten Karte aus:
 *   nicht wissen ist kein Grund, sie wegzunehmen.
 * @param route der Bereich der Reise, oder null bei einer Reise ohne Etappen.
 */
export function basemapDeckt(archiv: Bounds | null, route: Bounds | null): boolean {
  if (!archiv || !route) return true
  const [[aWest, aSued], [aOst, aNord]] = archiv
  const [[rWest, rSued], [rOst, rNord]] = route
  return aWest <= rOst && rWest <= aOst && aSued <= rNord && rSued <= aNord
}

/**
 * Was von einem 'data'-Ereignis von MapLibre gebraucht wird.
 *
 * Absichtlich nur diese vier Felder und nicht der Typ von MapLibre: so ist die
 * Entscheidung darunter ohne Karte und ohne WebGL prüfbar.
 */
export interface MapDataSignal {
  dataType: string
  sourceId?: string
  sourceDataType?: string
  isSourceLoaded?: boolean
}

/**
 * Hebt dieses Ereignis die Warnung über fehlende Kacheln wieder auf?
 *
 * Ein Kachelfehler ist meist vorübergehend - eine Unterführung, ein Funkloch,
 * ein Zeitüberschritt. Die Warnung darf deshalb nicht stehen bleiben, bis der
 * Reiter gewechselt wird, sondern nur so lange, wie sie stimmt.
 *
 * Aufgehoben wird sie einzig durch die Basiskarte selbst: die Route liegt als
 * GeoJSON im Stil und ist immer sofort "geladen", sie würde jede Warnung
 * unbesehen wegwischen. Und erst bei isSourceLoaded, also wenn alle Kacheln des
 * Ausschnitts da sind - eine einzelne nachgeladene Kachel sagt nichts darüber,
 * ob die Karte wieder vollstaendig ist.
 */
export function clearsTileWarning(event: MapDataSignal, basemapSource: string): boolean {
  if (event.dataType !== 'source') return false
  if (event.sourceId !== basemapSource) return false
  if (event.sourceDataType !== 'content') return false
  return event.isSourceLoaded === true
}

/**
 * Ergebnis der WebGL-Probe, einmal gemerkt.
 *
 * Nicht nur zur Ersparnis: jede Probe belegt einen WebGL-Kontext, und Browser
 * halten nur acht bis sechzehn davon gleichzeitig. Bei jedem Reiterwechsel
 * einen neuen anzulegen heisst, das Kontingent gegen die eigene Karte zu
 * verbrauchen - der älteste Kontext geht dabei still verloren.
 */
let webglGeprueft: boolean | null = null

/**
 * Prüft, ob der Browser WebGL kann.
 *
 * MapLibre wirft beim Erzeugen der Karte, wenn kein Kontext zu bekommen ist -
 * in einer React-Komponente also mitten im Aufbau. Vorher fragen ist
 * freundlicher: dann erscheint die schematische Karte, statt dass der Reiter
 * leer bleibt.
 */
export function hasWebgl(): boolean {
  webglGeprueft ??= probeWebgl()
  return webglGeprueft
}

function probeWebgl(): boolean {
  if (typeof document === 'undefined') return false
  try {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('webgl2') ?? canvas.getContext('webgl')
    if (!context) return false
    // Den Probe-Kontext ausdrücklich zurückgeben. Er wird nie wieder
    // gebraucht, würde aber bis zur nächsten Aufräumrunde des Browsers gegen
    // dasselbe Kontingent zählen wie die Karte, die gleich danach entsteht.
    context.getExtension('WEBGL_lose_context')?.loseContext()
    return true
  } catch {
    // Manche Umgebungen werfen hier statt null zu liefern.
    return false
  }
}
